/**
 * FormV2 — the shared v2 form pattern (Phase 1 fan-out foundation).
 *
 * The recurring labeled-field / grouped-section form structure the canonical
 * spec repeats on JD new/edit, Candidate new, and Settings
 * (design/v2-direction/index.html `.input`/`.card`/`.card-head`/`.card-body`,
 * lines 332-341, 378-387). Three cooperating pieces:
 *
 *   • <FormSectionV2>  — a titled section Card grouping related fields
 *                        (canonical `.card` + `.card-head` + `.card-body`).
 *   • <FormFieldV2>    — a labeled field row: <Label> + control + optional
 *                        hint / error, with the label wired to the control id.
 *   • <FormActionsV2>  — the right-aligned Cancel / Save action row.
 *
 * FormFieldV2 renders the label + slots the caller's control (Input, Select,
 * Textarea, Switch, Checkbox — all the vendored v2 primitives). It does NOT
 * hard-code a control, so any batch composes the field it needs.
 *
 * HOW A BATCH USES THIS
 *   <FormSectionV2 title="Role basics" description="Core details for this job">
 *     <FormFieldV2 label="Job title" htmlFor="title">
 *       <Input id="title" … />
 *     </FormFieldV2>
 *     <div className="grid grid-cols-2 gap-4">
 *       <FormFieldV2 label="Department" htmlFor="dept"><Select …/></FormFieldV2>
 *       <FormFieldV2 label="Level" htmlFor="level"><Select …/></FormFieldV2>
 *     </div>
 *   </FormSectionV2>
 *   <FormActionsV2>
 *     <Button variant="outline">Cancel</Button><Button>Save job</Button>
 *   </FormActionsV2>
 *
 * Density: card p-5, field gap-1.5, section stack gap-4 — under the §2.0 ceiling.
 * Token discipline: no hex / rgb / hsl(var()); semantic utilities + tokenized
 * primitives only. Renders in light + dark.
 */
import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** A titled section grouping related fields. */
export function FormSectionV2({
  title,
  description,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <Card className={className}>
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle className="text-[15px]">{title}</CardTitle>}
          {description && (
            <CardDescription className="text-[13px]">
              {description}
            </CardDescription>
          )}
        </CardHeader>
      )}
      <CardContent
        className={cn("flex flex-col gap-4", !title && !description && "pt-5")}
      >
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * A labeled field row. Renders the Label (wired to `htmlFor`), the caller's
 * control, and an optional hint or error line. Pass `required` for the asterisk.
 */
export function FormFieldV2({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="ml-0.5 text-verdict-gap-fg" aria-hidden>
            *
          </span>
        )}
      </Label>
      {children}
      {error ? (
        <p className="text-[12px] text-verdict-gap-fg">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-text-tertiary">{hint}</p>
      ) : null}
    </div>
  );
}

/** Right-aligned action row (Cancel / Save …), separated from the form body. */
export function FormActionsV2({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t border-border-default pt-4",
        className,
      )}
    >
      {children}
    </div>
  );
}
